import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { participantFragment } from '../fragments/commonData.fragments';
import { errorToast } from 'basics/utils/toast';
import { RegisterParticipantInput } from 'generated/event.types';
import { Participant } from 'modules/events/basics/types/events.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type UseCancelParticipantMutationResponse = {
  cancelParticipantRegistration: Participant;
};

export type UseCancelParticipantMutation = [
  (participant: RegisterParticipantInput) => Promise<Participant | null>,
  MutationResult<UseCancelParticipantMutationResponse>,
];
export const deleteParticipantMutationCall = gql`
mutation cancelParticipantRegistration($participant: RegisterParticipantInput!) {
    cancelParticipantRegistration(participant: $participant) {
     ${participantFragment}
    }
}`;

export const useCancelParticipantMutation = (): UseCancelParticipantMutation => {
  const { t } = useTranslation();
  const [cancelRegistrationMutation, state] = useMutation<UseCancelParticipantMutationResponse>(deleteParticipantMutationCall);

  const executeCancelRegistrationMutation = useCallback(async (participant: RegisterParticipantInput) => {
    try {
      const { data } = await cancelRegistrationMutation({
        variables: { participant },
      });
      if (!data?.cancelParticipantRegistration) {
        errorToast(t('errors_standard'));
        return null;
      }
      return data?.cancelParticipantRegistration;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'deleteParticipant.ts - useCancelParticipantMutation',
        });
        errorToast(t('errors_standard'));
      }
      return null;
    }
  }, [cancelRegistrationMutation, t]);

  return [executeCancelRegistrationMutation, state];
};
